<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    name: string;
    size?: number | string;
    strokeWidth?: number | string;
    class?: string;
  }>(),
  {
    size: 20,
    strokeWidth: 1.75,
    class: "",
  },
);
</script>

<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    :class="['inline-block shrink-0', props.class]"
  >
    <!-- Pencil -->
    <template v-if="name === 'pencil'">
      <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
      <path d="M13.5 6.5l4 4" />
    </template>

    <!-- Droplet -->
    <template v-else-if="name === 'droplet'">
      <path d="M7.502 19.423c2.602 2.105 6.394 2.105 8.996 0c2.602 -2.105 3.262 -5.708 1.566 -8.546l-4.89 -7.26a.749 .749 0 0 0 -1.274 0l-4.89 7.26c-1.696 2.838 -1.036 6.441 1.492 8.546z" />
    </template>

    <!-- Stack-2 -->
    <template v-else-if="name === 'stack-2'">
      <path d="M12 4l-8 4l8 4l8 -4l-8 -4" />
      <path d="M4 12l8 4l8 -4" />
      <path d="M4 16l8 4l8 -4" />
    </template>

    <!-- Flask -->
    <template v-else-if="name === 'flask'">
      <path d="M9 3l6 0" />
      <path d="M10 9l4 0" />
      <path d="M10 3v6l-4 11a.7 .7 0 0 0 .5 1h13a.7 .7 0 0 0 .5 -1l-4 -11v-6" />
    </template>

    <!-- Sun -->
    <template v-else-if="name === 'sun'">
      <path d="M12 12m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
      <path d="M3 12h1m8 -9v1m8 8h1m-9 8v1m-6.4 -15.4l.7 .7m12.1 -.7l-.7 .7m0 11.4l.7 .7m-12.1 -.7l-.7 .7" />
    </template>

    <!-- Photo -->
    <template v-else-if="name === 'photo'">
      <path d="M15 8h.01" />
      <path d="M3 6a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v12a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3v-12z" />
      <path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l5 5" />
      <path d="M14 14l1 -1c.928 -.893 2.072 -.893 3 0l3 3" />
    </template>

    <!-- Cut / Scissors -->
    <template v-else-if="name === 'cut' || name === 'scissors'">
      <path d="M6 7m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
      <path d="M6 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
      <path d="M8.6 8.6l10.4 10.4" />
      <path d="M8.6 15.4l10.4 -10.4" />
    </template>

    <!-- Square -->
    <template v-else-if="name === 'square'">
      <path d="M4 4m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z" />
    </template>

    <!-- Arrow-big-right -->
    <template v-else-if="name === 'arrow-big-right'">
      <path d="M4 9h8v-3.586a1 1 0 0 1 1.707 -.707l6.586 6.586a1 1 0 0 1 0 1.414l-6.586 6.586a1 1 0 0 1 -1.707 -.707v-3.586h-8a1 1 0 0 1 -1 -1v-4a1 1 0 0 1 1 -1z" />
    </template>

    <!-- Brush -->
    <template v-else-if="name === 'brush'">
      <path d="M3 21v-4a4 4 0 1 1 4 4h-4" />
      <path d="M21 3a16 16 0 0 0 -12.8 10.2" />
      <path d="M21 3a16 16 0 0 1 -10.2 12.8" />
      <path d="M10.6 9a9 9 0 0 1 4.4 4.4" />
    </template>

    <!-- Sparkles -->
    <template v-else-if="name === 'sparkles'">
      <path d="M16 18a2 2 0 0 1 2 2a2 2 0 0 1 2 -2a2 2 0 0 1 -2 -2a2 2 0 0 1 -2 2zm0 -12a2 2 0 0 1 2 2a2 2 0 0 1 2 -2a2 2 0 0 1 -2 -2a2 2 0 0 1 -2 2zm-7 3a6 6 0 0 1 6 6a6 6 0 0 1 -6 6a6 6 0 0 1 -6 -6a6 6 0 0 1 6 -6z" />
    </template>

    <!-- Arrow-down -->
    <template v-else-if="name === 'arrow-down'">
      <path d="M12 5l0 14" />
      <path d="M18 13l-6 6" />
      <path d="M6 13l6 6" />
    </template>

    <!-- Arrows-left-right -->
    <template v-else-if="name === 'arrows-left-right'">
      <path d="M21 17l-18 0" />
      <path d="M6 10l-3 3l3 3" />
      <path d="M3 7l18 0" />
      <path d="M18 4l3 3l-3 3" />
    </template>

    <!-- Hammer -->
    <template v-else-if="name === 'hammer'">
      <path d="M11.414 10l-7.383 7.418a2.091 2.091 0 0 0 0 2.967a2.11 2.11 0 0 0 2.976 0l7.407 -7.385" />
      <path d="M18.121 15.293l2.586 -2.586a1 1 0 0 0 0 -1.414l-7.586 -7.586a1 1 0 0 0 -1.414 0l-2.586 2.586a1 1 0 0 0 0 1.414l7.586 7.586a1 1 0 0 0 1.414 0z" />
    </template>

    <!-- Flame -->
    <template v-else-if="name === 'flame'">
      <path d="M12 12c2 -2.96 0 -7 -1 -8c0 3.038 -1.773 4.741 -3 6c-1.226 1.26 -2 3.24 -2 5a6 6 0 1 0 12 0c0 -1.532 -1.056 -3.94 -2 -5c-1.786 3 -2.791 3 -4 2z" />
    </template>

    <!-- Box -->
    <template v-else-if="name === 'box'">
      <path d="M12 3l8 4.5l0 9l-8 4.5l-8 -4.5l0 -9l8 -4.5" />
      <path d="M12 12l8 -4.5" />
      <path d="M12 12l0 9" />
      <path d="M12 12l-8 -4.5" />
    </template>

    <!-- Shape / Wax model -->
    <template v-else-if="name === 'shape'">
      <path d="M12 3l8 4.5v9l-8 4.5l-8 -4.5v-9l8 -4.5z" />
      <path d="M12 12l-6 -3.5" />
      <path d="M12 12l6 -3.5" />
      <path d="M12 12v7" />
    </template>

    <!-- Shirt -->
    <template v-else-if="name === 'shirt'">
      <path d="M15 4l6 2v5h-3v8a1 1 0 0 1 -1 1h-10a1 1 0 0 1 -1 -1v-8h-3v-5l6 -2a3 3 0 0 0 6 0" />
    </template>

    <!-- Grid-dots -->
    <template v-else-if="name === 'grid-dots'">
      <path d="M5 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M12 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M19 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M5 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M19 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M5 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M12 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
      <path d="M19 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
    </template>

    <!-- Circle-dot -->
    <template v-else-if="name === 'circle-dot'">
      <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
      <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
    </template>

    <!-- Layout-grid -->
    <template v-else-if="name === 'layout-grid'">
      <path d="M4 4m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />
      <path d="M14 4m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />
      <path d="M4 14m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />
      <path d="M14 14m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z" />
    </template>

    <!-- Puzzle -->
    <template v-else-if="name === 'puzzle'">
      <path d="M4 4m0 2a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v3.5a1.5 1.5 0 0 0 3 0v-3.5a2 2 0 0 1 2 -2h.5a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-3.5a1.5 1.5 0 0 0 0 3v.5a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-3.5a1.5 1.5 0 0 0 -3 0v-3.5a2 2 0 0 1 -2 -2z" />
    </template>

    <!-- Cube -->
    <template v-else-if="name === 'cube'">
      <path d="M21 16.05v-8.1a2 2 0 0 0 -1 -1.73l-7 -4a2 2 0 0 0 -2 0l-7 4a2 2 0 0 0 -1 1.73v8.2a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7 -4a2 2 0 0 0 1 -1.73z" />
      <path d="M3.27 6.96l8.73 4.04l8.73 -4.04" />
      <path d="M12 22.08v-11.08" />
    </template>

    <!-- Rotate-3d -->
    <template v-else-if="name === 'rotate-3d'">
      <path d="M12 3a9 9 0 0 1 9 9h-3a6 6 0 0 0 -6 -6v-3z" />
      <path d="M12 21a9 9 0 0 1 -9 -9h3a6 6 0 0 0 6 6v3z" />
      <path d="M19 12l2.5 -2.5" />
      <path d="M19 12l2.5 2.5" />
      <path d="M5 12l-2.5 -2.5" />
      <path d="M5 12l-2.5 2.5" />
    </template>

    <!-- Printer-3d -->
    <template v-else-if="name === 'printer-3d'">
      <path d="M4 6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z" />
      <path d="M12 12v5" />
      <path d="M9 17h6" />
      <path d="M4 17l8 4l8 -4" />
    </template>

    <!-- Video -->
    <template v-else-if="name === 'video'">
      <path d="M15 10l4.553 -2.276a1 1 0 0 1 1.447 .894v6.764a1 1 0 0 1 -1.447 .894l-4.553 -2.276v-4z" />
      <path d="M3 6a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z" />
    </template>

    <!-- Player-play -->
    <template v-else-if="name === 'player-play'">
      <path d="M7 4v16l13 -8z" />
    </template>

    <!-- Scan -->
    <template v-else-if="name === 'scan'">
      <path d="M4 7v-1a2 2 0 0 1 2 -2h2" />
      <path d="M4 17v1a2 2 0 0 0 2 2h2" />
      <path d="M16 4h2a2 2 0 0 1 2 2v1" />
      <path d="M16 20h2a2 2 0 0 0 2 -2v-1" />
      <path d="M5 12l14 0" />
    </template>

    <!-- Printer -->
    <template v-else-if="name === 'printer'">
      <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
      <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
      <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
    </template>

    <!-- X (Close) -->
    <template v-else-if="name === 'x'">
      <path d="M18 6l-12 12" />
      <path d="M6 6l12 12" />
    </template>

    <!-- Default fallback circle -->
    <template v-else>
      <circle cx="12" cy="12" r="9" />
    </template>
  </svg>
</template>
